import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Reveal";
import { CtaButtons, Faq, JsonLd } from "@/components/site/Seo";
import { SERVICE_BY_SLUG, MODALITES_COMMUNES } from "@/data/services";
import { COMPANY } from "@/data/site";
import usePageMeta from "@/hooks/usePageMeta";

const H2 = ({ children }) => <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">{children}</h2>;

const Related = ({ slugs }) => (
  <div className="grid gap-4 sm:grid-cols-3" data-testid="service-related">
    {slugs.map((k) => {
      const r = SERVICE_BY_SLUG[k];
      return (
        <Link key={k} to={r.path} data-testid={`related-${k}`} className="group rounded-lg border border-[#0F2840]/10 p-5 transition-colors hover:border-[#D82C2C]">
          <r.icon className="h-6 w-6 text-[#D82C2C]" />
          <h3 className="mt-3 font-bold group-hover:text-[#D82C2C]">{r.nom} à Brest</h3>
          <p className="mt-1 text-xs text-slate-500">{r.intro.slice(0, 90)}…</p>
        </Link>
      );
    })}
  </div>
);

export default function ServicePage({ slug: fixed }) {
  const params = useParams();
  const s = SERVICE_BY_SLUG[fixed || params.slug];
  usePageMeta(s.title, s.meta);
  const url = `${COMPANY.site}${s.path}`;
  return (
    <>
      <JsonLd data={{ "@context": "https://schema.org", "@type": "Service", name: s.nom, serviceType: s.nom, description: s.meta, url,
        areaServed: [{ "@type": "City", name: "Brest" }, { "@type": "AdministrativeArea", name: "Finistère" }],
        provider: { "@type": "LocalBusiness", name: COMPANY.nom, telephone: "+33766488659", url: `${COMPANY.site}/` } }} />
      <PageHero testid={`service-${s.slug}-hero`} eyebrow={`${s.nom} · Brest`} title={s.h1} text={s.intro} image={s.image} />
      <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-12 lg:px-8" data-testid={`service-${s.slug}`}>
        <div className="space-y-12 md:col-span-8">
          <CtaButtons id={`service-${s.slug}-top`} />
          <Reveal>
            <H2>Ce que comprend la prestation</H2>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {s.inclus.map((x) => <li key={x} className="flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D82C2C]" />{x}</li>)}
            </ul>
          </Reveal>
          {s.sections.map((sec) => (
            <Reveal key={sec.h2}><H2>{sec.h2}</H2>{sec.p.map((p) => <p key={p.slice(0, 20)} className="mt-4 leading-relaxed text-slate-700">{p}</p>)}</Reveal>
          ))}
          <Reveal>
            <H2>Exemples de prestations</H2>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {s.exemples.map((e) => <div key={e.h3} className="rounded-lg bg-slate-50 p-5"><h3 className="font-bold">{e.h3}</h3><p className="mt-2 text-sm text-slate-600">{e.p}</p></div>)}
            </div>
          </Reveal>
          <Reveal>
            <H2>Modalités d'intervention</H2>
            <ol className="mt-5 space-y-3">
              {MODALITES_COMMUNES.map((m, i) => <li key={m} className="flex gap-3 text-sm text-slate-700"><span className="font-bold text-[#D82C2C]">{i + 1}.</span>{m}</li>)}
            </ol>
          </Reveal>
          <Reveal><H2>Questions fréquentes</H2><div className="mt-3"><Faq items={s.faq} id={`service-${s.slug}`} /></div></Reveal>
          <Reveal><H2>Nos autres services à Brest</H2><div className="mt-5"><Related slugs={s.related} /></div></Reveal>
        </div>
        <aside className="md:col-span-4">
          <div className="sticky top-28 space-y-4 rounded-lg bg-[#0F2840] p-7 text-white">
            <img src={s.image} alt={s.alt} loading="lazy" width="1100" height="733" className="h-44 w-full rounded-md object-cover" />
            <p className="font-display text-xl">Un besoin pour votre logement ?</p>
            <p className="text-sm text-white/75">{COMPANY.horaires}. Devis gratuit et sans engagement.</p>
            <CtaButtons id={`service-${s.slug}-aside`} light />
            <Link to="/tarifs-conciergerie-brest/" className="block text-sm font-semibold text-[#ff8080] underline" data-testid={`service-${s.slug}-tarifs-link`}>Voir nos tarifs</Link>
          </div>
        </aside>
      </section>
    </>
  );
}
